import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Wrench, Plus, Mail, Phone, Trash2, Pencil, Loader2, Tag, PhoneCall, Building2 } from "lucide-react";
import PageTour from "@/components/PageTour";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const EMPTY = { name: "", email: "", phone: "", category: "", notes: "", visible_to_condomini: false, all_buildings: false, building_ids: [] };

export default function Fornitori() {
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [catFilter, setCatFilter] = useState("tutti");
  const [buildings, setBuildings] = useState([]);

  const load = useCallback(() => api.get("/suppliers").then((r) => setSuppliers(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/buildings").then((r) => setBuildings(r.data || [])).catch(() => {}); }, []);

  const categories = [...new Set(suppliers.map((s) => (s.category || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const filtered = catFilter === "tutti" ? suppliers : suppliers.filter((s) => (s.category || "").trim() === catFilter);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ name: s.name || "", email: s.email || "", phone: s.phone || "", category: s.category || "", notes: s.notes || "", visible_to_condomini: !!s.visible_to_condomini, all_buildings: !!s.all_buildings, building_ids: s.building_ids || [] }); setEditId(s.id); setOpen(true); };

  const toggleBuilding = (id) => setForm((f) => ({ ...f, building_ids: f.building_ids.includes(id) ? f.building_ids.filter((x) => x !== id) : [...f.building_ids, id] }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Il nome del fornitore è obbligatorio");
    setBusy(true);
    try {
      if (editId) await api.put(`/suppliers/${editId}`, form);
      else await api.post("/suppliers", form);
      toast.success(editId ? "Fornitore aggiornato" : "Fornitore aggiunto");
      setOpen(false);
      load();
    } catch { toast.error("Errore nel salvataggio del fornitore"); }
    finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/suppliers/${toDelete.id}`);
      toast.success(`Fornitore "${toDelete.name}" eliminato`);
      setToDelete(null);
      load();
    } catch { toast.error("Errore durante l'eliminazione"); }
  };

  return (
    <div className="fade-in">
      <header className="px-4 sm:px-10 pt-4 sm:pt-6 pb-4 border-b border-border bg-card/50">
        <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-1.5">Rubrica</div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tighter">Fornitori</h1>
            <p className="text-muted-foreground text-sm mt-1">Anagrafica fornitori a cui inviare i ticket per la presa in carico degli interventi.</p>
            <PageTour storageKey="tour_fornitori" className="mt-2" steps={[
              { title: "Rubrica Fornitori 🧰", body: "Ti mostro come gestire i fornitori e renderli disponibili ai condòmini. Esci con la X, riapri con \"Guida\"." },
              { selector: "add-supplier-button", title: "Aggiungi un fornitore", body: "Inserisci nome, categoria e contatti. I fornitori qui salvati potranno ricevere i ticket degli interventi." },
              { selector: "supplier-category-filter", title: "Filtra per categoria", body: "Trova velocemente i fornitori filtrando per categoria (idraulico, elettricista, ascensori…)." },
              { title: "Numeri utili per i condòmini 📞", body: "Nel form del fornitore trovi l'interruttore \"Numeri utili — visibile ai condòmini\": abilitalo e scegli i condomìni per farlo comparire nel loro portale, così potranno chiamarlo direttamente." },
              { title: "Fatto! 🎉", body: "Riavvia questa guida quando vuoi dal pulsante \"Guida\" in alto." },
            ]} />
          </div>
          <Button data-testid="add-supplier-button" onClick={openNew} className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-11 shrink-0">
            <Plus className="h-4 w-4 mr-2" />Nuovo fornitore
          </Button>
        </div>
      </header>

      <div className="p-4 sm:p-10">
        {suppliers.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground flex flex-col items-center gap-3">
            <Wrench className="h-10 w-10 opacity-40" />
            Nessun fornitore. Aggiungi il primo fornitore per poterlo assegnare ai ticket.
          </div>
        ) : (
          <>
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="supplier-category-filter">
                <button data-testid="supplier-cat-tutti" onClick={() => setCatFilter("tutti")}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${catFilter === "tutti" ? "bg-[#007BFF] text-white border-[#007BFF]" : "border-border text-muted-foreground hover:border-[#007BFF] hover:text-[#007BFF]"}`}>
                  Tutte ({suppliers.length})
                </button>
                {categories.map((cat) => {
                  const n = suppliers.filter((s) => (s.category || "").trim() === cat).length;
                  return (
                    <button key={cat} data-testid={`supplier-cat-${cat}`} onClick={() => setCatFilter(cat)}
                      className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${catFilter === cat ? "bg-[#007BFF] text-white border-[#007BFF]" : "border-border text-muted-foreground hover:border-[#007BFF] hover:text-[#007BFF]"}`}>
                      <Tag className="h-3 w-3" />{cat} ({n})
                    </button>
                  );
                })}
              </div>
            )}
            {filtered.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">Nessun fornitore nella categoria "{catFilter}".</div>
            ) : (
              <div data-testid="suppliers-list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((s, i) => (
                  <div key={s.id} data-testid={`supplier-card-${i}`} className="list-enter group bg-card border border-border rounded-md p-5 hover:border-foreground/30 transition-colors duration-150" style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-md bg-[#007BFF]/10 flex items-center justify-center shrink-0"><Wrench className="h-5 w-5 text-[#007BFF]" /></div>
                        <div className="min-w-0">
                          <h4 className="font-semibold tracking-tight truncate">{s.name}</h4>
                          {s.category && <span className="text-[10px] uppercase tracking-wider bg-secondary text-muted-foreground px-1.5 py-0.5 rounded inline-flex items-center gap-1 mt-1"><Tag className="h-2.5 w-2.5" />{s.category}</span>}
                          {s.visible_to_condomini && <span data-testid={`supplier-visible-badge-${i}`} className="text-[10px] uppercase tracking-wider bg-[#007BFF]/10 text-[#007BFF] px-1.5 py-0.5 rounded inline-flex items-center gap-1 mt-1 ml-1"><PhoneCall className="h-2.5 w-2.5" />Numeri utili</span>}
                          {(() => {
                            const names = (s.building_ids || []).map((id) => buildings.find((b) => b.id === id)?.name).filter(Boolean);
                            if (s.all_buildings) {
                              return <span data-testid={`supplier-scope-badge-${i}`} title="Fornitore associato a tutti i condomìni" className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1 mt-1 ml-1"><Building2 className="h-2.5 w-2.5" />Tutti i condomìni</span>;
                            }
                            if ((s.building_ids || []).length > 0) {
                              const n = s.building_ids.length;
                              return <span data-testid={`supplier-scope-badge-${i}`} title={names.length ? `Associato a: ${names.join(", ")}` : `${n} condomìni selezionati`} className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1 mt-1 ml-1"><Building2 className="h-2.5 w-2.5" />{n} condomìn{n === 1 ? "io" : "i"}</span>;
                            }
                            return <span data-testid={`supplier-scope-badge-${i}`} title="Nessun condominio associato" className="text-[10px] uppercase tracking-wider bg-secondary text-muted-foreground px-1.5 py-0.5 rounded inline-flex items-center gap-1 mt-1 ml-1"><Building2 className="h-2.5 w-2.5" />Nessun condominio</span>;
                          })()}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shrink-0">
                        <button data-testid={`edit-supplier-${i}`} onClick={() => openEdit(s)} className="p-1.5 rounded text-muted-foreground hover:text-[#007BFF]"><Pencil className="h-4 w-4" /></button>
                        <button data-testid={`delete-supplier-${i}`} onClick={() => setToDelete(s)} className="p-1.5 rounded text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                    <div className="mt-4 space-y-1.5 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{s.email || "—"}</span></div>
                      <div className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{s.phone || "—"}</span></div>
                    </div>
                    {s.notes && <p className="mt-3 text-xs text-muted-foreground bg-secondary/50 rounded p-2 leading-relaxed">{s.notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="supplier-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editId ? "Modifica fornitore" : "Nuovo fornitore"}</DialogTitle>
            <DialogDescription className="sr-only">Dati anagrafici del fornitore</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Nome / Ragione sociale *</Label>
              <Input data-testid="supplier-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Idraulica Rossi S.r.l." className="rounded-md mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Categoria</Label>
              <Input data-testid="supplier-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Idraulico, Elettricista, Ascensori…" className="rounded-md mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input data-testid="supplier-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="fornitore@email.it" className="rounded-md mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Telefono</Label>
              <Input data-testid="supplier-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 …" className="rounded-md mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Note</Label>
              <Textarea data-testid="supplier-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-md mt-1.5 resize-none" /></div>

            <div className="rounded-md border border-border p-3 space-y-3">
              <label className="flex items-start gap-2.5 cursor-pointer" data-testid="supplier-visible-toggle">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#007BFF]" checked={form.visible_to_condomini}
                  onChange={(e) => setForm({ ...form, visible_to_condomini: e.target.checked })} />
                <span>
                  <span className="text-sm font-medium flex items-center gap-1.5"><PhoneCall className="h-3.5 w-3.5 text-[#007BFF]" />Numeri utili — visibile ai condòmini</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">Il fornitore comparirà tra i "Numeri utili" nel portale dei condòmini selezionati.</span>
                </span>
              </label>
              {form.visible_to_condomini && (
                <div className="pl-6 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="supplier-all-buildings">
                    <input type="checkbox" className="h-4 w-4 accent-[#007BFF]" checked={form.all_buildings}
                      onChange={(e) => setForm({ ...form, all_buildings: e.target.checked })} />
                    <span className="text-sm font-medium">Tutti i condomìni</span>
                  </label>
                  {!form.all_buildings && (
                    buildings.length === 0 ? (
                      <p className="text-xs text-muted-foreground">Nessun condominio disponibile.</p>
                    ) : (
                      <div className="max-h-40 overflow-y-auto rounded-md border border-border divide-y divide-border/60" data-testid="supplier-buildings-list">
                        {buildings.map((b) => (
                          <label key={b.id} className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-secondary/50" data-testid={`supplier-building-${b.id}`}>
                            <input type="checkbox" className="h-3.5 w-3.5 accent-[#007BFF]" checked={form.building_ids.includes(b.id)} onChange={() => toggleBuilding(b.id)} />
                            <span className="text-sm truncate">{b.name}</span>
                          </label>
                        ))}
                      </div>
                    )
                  )}
                  {!form.all_buildings && form.building_ids.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">{form.building_ids.length} condominio/i selezionato/i</p>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Annulla</Button>
            <Button data-testid="save-supplier-button" onClick={save} disabled={busy} className="rounded-md bg-[#007BFF] hover:bg-[#0056B3]">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-supplier-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il fornitore?</AlertDialogTitle>
            <AlertDialogDescription>Stai per eliminare <span className="font-semibold text-foreground">{toDelete?.name}</span>. L'operazione non è reversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-supplier">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-supplier" onClick={(e) => { e.preventDefault(); confirmDelete(); }} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
